import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Button, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { type FieldPath, useFormContext, useWatch } from 'react-hook-form';
import { FormSwitch } from 'src/components/Forms';
import {
  planControlFormSx,
  PlansCounterChip,
  planToggleLabelSx,
  planToggleSx,
  SectionAccordion,
  SectionAccordionDetails,
  SectionAccordionSummary,
  SectionSummaryContent,
  SectionSummaryTitle,
  SectionWrapper,
} from 'src/pages/Settings/memberships/components/plans-tab/styled';
import type { PlansTabFormValues } from 'src/pages/Settings/memberships/components/plans-tab/types';

const addFaqButtonSx = {
  alignSelf: 'flex-start',
  textTransform: 'none',
  fontWeight: 500,
} as const;

type FaqSectionProps = {
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  totalCount: number;
  showFaqFieldName: FieldPath<PlansTabFormValues>;
  onAddFaq: () => void;
  readOnly: boolean;
  children?: ReactNode;
};

export const FaqSection = ({
  expanded,
  onExpandedChange,
  totalCount,
  showFaqFieldName,
  onAddFaq,
  readOnly,
  children,
}: FaqSectionProps) => {
  const { control } = useFormContext<PlansTabFormValues>();
  const showFaq = (useWatch({ control, name: showFaqFieldName }) as boolean | undefined) ?? false;
  const visibleCount = showFaq ? totalCount : 0;

  return (
    <SectionWrapper data-testid="faq-section">
      <SectionAccordion
        elevation={0}
        expanded={expanded}
        TransitionProps={{ mountOnEnter: true, unmountOnExit: true }}
        onChange={(_, expandedValue) => {
          onExpandedChange(expandedValue);
        }}
        disableGutters
      >
        <SectionAccordionSummary expandIcon={<ChevronRightIcon />}>
          <SectionSummaryContent>
            <SectionSummaryTitle variant="h6">FAQs</SectionSummaryTitle>
            <PlansCounterChip label={`${visibleCount}/${totalCount}`} size="small" />
          </SectionSummaryContent>
        </SectionAccordionSummary>

        <SectionAccordionDetails>
          {expanded ? (
            <Stack gap={1.5} pt={1}>
              <FormSwitch
                type="switch"
                name={showFaqFieldName}
                label={<Typography sx={planToggleSx}>Show FAQ section</Typography>}
                labelPlacement="start"
                sx={planControlFormSx}
                labelSx={planToggleLabelSx}
                switchFieldProps={{
                  size: 'small',
                  sx: planToggleSx,
                  inputProps: { 'aria-label': 'Show FAQ section toggle' },
                }}
                disabled={readOnly}
              />

              {children}

              {!readOnly ? (
                <Button
                  startIcon={<AddIcon />}
                  variant="text"
                  onClick={onAddFaq}
                  sx={addFaqButtonSx}
                >
                  Add FAQ
                </Button>
              ) : null}
            </Stack>
          ) : null}
        </SectionAccordionDetails>
      </SectionAccordion>
    </SectionWrapper>
  );
};
